import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  changeConnectedToGraph,
  changePassword,
  changePasswordError,
  changePasswordSuccess,
  connectedToGraphSuccess,
  createUnpaidInvoicePaymentLink,
  createUnpaidInvoicePaymentLinkError,
  downloadSignature,
  downloadSignatureError,
  downloadSignatureSuccess,
  loadCreditsLimitations,
  loadCreditsLimitationsError,
  loadCreditsLimitationsSuccess,
  loadCreditsMaxLimit,
  loadCreditsMaxLimitError,
  loadCreditsMaxLimitSuccess,
  loadTrialDurationInDays,
  loadTrialDurationInDaysError,
  loadTrialDurationInDaysSuccess,
  loadUserProfile,
  loadUserProfileError,
  loadUserProfileSuccess,
  loadUsers,
  loadUsersError,
  loadUsersSuccess,
  loadUserSubscriptionDetails,
  loadUserSubscriptionDetailsError,
  loadUserSubscriptionDetailsSuccess,
  loadUserSubscriptionType,
  loadUserSubscriptionTypeError,
  loadUserSubscriptionTypeSuccess,
  manageBilling,
  setTrialDaysLeft,
  updateFirstAndLastNames,
  loadUsersForAdmin,
  loadUsersForAdminSuccess,
  loadUsersForAdminError,
  changeConnectedToGmail,
  connectedToGmailSuccess,
} from './users.actions';
import { EMPTY, filter, map, Observable, of, switchMap } from 'rxjs';
import { catchError, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { MeService } from '../../shared/services/me.service';
import { Action, Store } from '@ngrx/store';
import { UserService } from '@app/shared/services/user.service';
import { showSnackbar } from '@app/shared/snackbar/snackbar.actions';
import { getLoggedInUser } from './users.selectors';
import { AttachmentService } from '@app/shared/services/attachment.service';
import { SubscriptionType } from '@app/shared/models/subscription-type';
import { SubscriptionService } from '@app/shared/services/subscription.service';
import { SubType } from '@app/shared/models/sub-type';
import { CreditsInfoService } from '@app/shared/services/credits-info.service';
import { WorkspacesService } from '@app/shared/services/workspaces.service';
import { GLOBAL_PRIVILEGES } from '@app/core/injection-tokens/global-privileges.injection-token';
import { MojoPrivilegesService } from 'mojo-privileges';
import { PrivilegeConfig } from '@app/shared/config/privilege.config';

@Injectable()
export class UsersEffects {
  private actions$ = inject(Actions);
  private meService = inject(MeService);
  private userService = inject(UserService);
  private store = inject(Store);
  private attachmentService = inject(AttachmentService);
  private subscriptionService = inject(SubscriptionService);
  private creditsInfoService = inject(CreditsInfoService);
  private workspacesService = inject(WorkspacesService);
  private privilegesService: MojoPrivilegesService = inject(GLOBAL_PRIVILEGES);

  loadLoggedInUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserProfile),
      switchMap(() =>
        this.meService.getCurrentUserDetails().pipe(
          map((user) => loadUserProfileSuccess({ payload: { user } })),
          catchError((error) => of(loadUserProfileError({ error }))),
        ),
      ),
    ),
  );

  loadLoggedInUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadUserProfileSuccess),
        tap(({ payload }) => {
          this.privilegesService.setUserPrivileges(payload.user.resources ?? []);
        }),
      ),
    { dispatch: false },
  );

  load$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUsers),
      switchMap(({ search, page }) =>
        this.userService.getUsers(search, page).pipe(
          map((usersPagination) => {
            return loadUsersSuccess({
              usersPagination,
            });
          }),
          catchError((error) => of(loadUsersError({ error }))),
        ),
      ),
    ),
  );

  changeConnectedToGraph$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changeConnectedToGraph),
      switchMap(({ connectedToGraph, userId }) => {
        return this.userService.changeConnectedToGraph(userId, connectedToGraph).pipe(
          mergeMap(() => [loadUserProfile(), connectedToGraphSuccess({ connectedToGraph })]),
          tap({ error: (error) => console.error('Connected to graph update failed', error) }),
          catchError(() => EMPTY),
        );
      }),
    ),
  );

  changeConnectedToGmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changeConnectedToGmail),
      switchMap(({ connectedToGmail, userId }) => {
        return this.userService.changeConnectedToGmail(userId, connectedToGmail).pipe(
          mergeMap(() => [loadUserProfile(), connectedToGmailSuccess({ connectedToGmail })]),
          tap({ error: (error) => console.error('Connected to gmail update failed', error) }),
          catchError(() => EMPTY),
        );
      }),
    ),
  );

  downloadSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(downloadSignature),
      withLatestFrom(this.store.select(getLoggedInUser)),
      switchMap(([, user]) =>
        this.attachmentService.downloadEmailSignature(user?.id).pipe(
          map((signature) => {
            return downloadSignatureSuccess({
              signature,
            });
          }),
          catchError((error) => of(downloadSignatureError({ error }))),
        ),
      ),
    ),
  );

  loadSubscriptionInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserSubscriptionType),
      switchMap(() =>
        this.subscriptionService.getSubscriptionInfo().pipe(
          switchMap((subType) => {
            if (subType?.type === SubType.TRIAL) {
              return of(
                loadTrialDurationInDays(),
                setTrialDaysLeft({ trialDaysLeft: this.calculateLeftDays(subType) }),
                loadUserSubscriptionTypeSuccess({ payload: { subscriptionType: subType } }),
              );
            }
            return of(loadUserSubscriptionTypeSuccess({ payload: { subscriptionType: subType } }));
          }),
          catchError((error) => of(loadUserSubscriptionTypeError({ error }))),
        ),
      ),
    ),
  );

  loadUserSubscriptionDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserSubscriptionDetails),
      withLatestFrom(this.privilegesService.hasPrivilege(PrivilegeConfig.MANAGE_SUBSCRIPTION)),
      filter(([, hasPrivilege]) => hasPrivilege),
      switchMap(() =>
        this.subscriptionService.getSubscriptionDetails().pipe(
          map((subscriptionDetails) =>
            loadUserSubscriptionDetailsSuccess({ payload: { subscriptionDetails } }),
          ),
          catchError((error) => of(loadUserSubscriptionDetailsError({ error }))),
        ),
      ),
    ),
  );

  loadUsersForAdmin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUsersForAdmin),
      withLatestFrom(this.privilegesService.hasPrivilege(PrivilegeConfig.MANAGE_WORKSPACE)),
      filter(([, hasPrivilege]) => hasPrivilege),
      switchMap(() =>
        this.workspacesService.getUsers().pipe(
          map((users) =>
            loadUsersForAdminSuccess({
              payload: { users },
            }),
          ),
          catchError((error) => of(loadUsersForAdminError({ error }))),
        ),
      ),
    ),
  );

  updateFirstAndLastNames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateFirstAndLastNames),
      withLatestFrom(this.store.select(getLoggedInUser)),
      switchMap(([action, user]) =>
        this.userService
          .updateFirstAndLastNames(action.payload.firstName, action.payload.lastName, user?.id)
          .pipe(
            switchMap((user) =>
              of(
                loadUserProfileSuccess({ payload: { user } }),
                showSnackbar({ payload: { message: 'Your name has been changed' } }),
              ),
            ),
            catchError((error) => of(loadUserProfileError({ error }))),
          ),
      ),
    ),
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changePassword),
      switchMap((action) =>
        this.userService
          .changePassword(action.payload.newPassword, action.payload.currentPassword)
          .pipe(
            map(() => changePasswordSuccess()),
            catchError((error) => of(changePasswordError({ error }))),
          ),
      ),
    ),
  );

  changePasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changePasswordSuccess),
      map(() => showSnackbar({ payload: { message: 'Password has been changed' } })),
    ),
  );

  manageBilling$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(manageBilling),
        switchMap(({ updateSubscription }) =>
          this.subscriptionService.createBillingPortalSession(updateSubscription).pipe(
            tap((response) => {
              window.open(response.checkoutUrl, '_blank');
            }),
            catchError(() => EMPTY),
          ),
        ),
      ),
    { dispatch: false },
  );

  createUnpaidInvoicePaymentLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createUnpaidInvoicePaymentLink),
      switchMap(() =>
        this.subscriptionService.createUnpaidInvoicePaymentLink().pipe(
          tap((response) => (window.location.href = response.checkoutUrl)),
          switchMap(() => EMPTY),
          catchError((error) => of(createUnpaidInvoicePaymentLinkError({ error }))),
        ),
      ),
    ),
  );

  private calculateLeftDays(subscriptionType: SubscriptionType): number | null {
    if (subscriptionType?.type != SubType.TRIAL || subscriptionType?.isBlocked) {
      return null;
    }
    const today = new Date();
    const endDate = new Date(subscriptionType.expiresAt);
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    const diffTime = endDate.getTime() - today.getTime();

    if (diffTime <= 0) {
      return 0;
    }

    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  loadTrialDurationInDays$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTrialDurationInDays),
      switchMap(() =>
        this.subscriptionService.getTrialDurationInDays().pipe(
          map((trialDurationInDays) =>
            loadTrialDurationInDaysSuccess({ payload: { trialDurationInDays } }),
          ),
          catchError((error) => of(loadTrialDurationInDaysError({ error }))),
        ),
      ),
    ),
  );

  loadCreditsLimitations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCreditsLimitations),
      switchMap(() =>
        this.creditsInfoService.getCreditsInfo().pipe(
          map((creditsLimitations) =>
            loadCreditsLimitationsSuccess({ payload: { creditsLimitations } }),
          ),
          catchError((error) => of(loadCreditsLimitationsError({ error }))),
        ),
      ),
    ),
  );

  loadCreditsMaxLimit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCreditsMaxLimit),
      switchMap(() =>
        this.creditsInfoService.getMaxCredits().pipe(
          map((creditsMaxLimit) => loadCreditsMaxLimitSuccess({ payload: { creditsMaxLimit } })),
          catchError((error) => of(loadCreditsMaxLimitError({ error }))),
        ),
      ),
    ),
  );
}
